
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed, effect } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe, JsonPipe } from '@angular/common'; 
import { PlatformService, PlatformStats, StoreSummary, GlobalActivityLog } from '../../services/platform.service';
import { AuthService } from '../../services/auth.service';
import { BillingService } from '../../services/billing.service';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../services/notification.service';
import { Plan } from '../../models/index';
import { SystemDiagnosticsComponent } from './system-diagnostics/system-diagnostics.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { ClearingHouseComponent } from './clearing-house/clearing-house.component'; 
import { PlatformTeamComponent } from './platform-team/platform-team.component';
import { PlatformMarketingComponent } from './platform-marketing/platform-marketing.component';
import { PlatformPlansComponent } from './platform-plans/platform-plans.component';

type SortDirection = 'asc' | 'desc';
type SortColumn = 'name' | 'created_at' | 'plan_name' | 'user_count' | 'sub_status' | 'wallet_balance';
type DashboardTab = 'overview' | 'clearing' | 'team' | 'plans' | 'logs' | 'marketing' | 'diagnostics'; 
type LogDatePreset = '7d' | '30d' | '90d';

@Component({
  selector: 'app-platform-dashboard',
  standalone: true,
  imports: [
    CommonModule, 
    DatePipe, 
    DecimalPipe, 
    JsonPipe, 
    FormsModule, 
    SystemDiagnosticsComponent, 
    TranslatePipe, 
    LanguageSwitcherComponent, 
    ConfirmationModalComponent, 
    ClearingHouseComponent, 
    PlatformTeamComponent,
    PlatformMarketingComponent,
    PlatformPlansComponent
  ],
  templateUrl: './platform-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlatformDashboardComponent implements OnInit {
  private platformService = inject(PlatformService);
  private authService = inject(AuthService);
  private billingService = inject(BillingService);
  private notificationService = inject(NotificationService);
  
  activeTab = signal<DashboardTab>('overview');

  stats = signal<PlatformStats | null>(null);
  stores = signal<StoreSummary[]>([]);
  plans = signal<Plan[]>([]); // Kept for filter dropdown in Overview
  logs = signal<GlobalActivityLog[]>([]);
  
  // -- Overview Tab State --
  searchQuery = signal<string>('');
  statusFilter = signal<string>('all');
  planFilter = signal<string>('all');
  sortColumn = signal<SortColumn>('created_at');
  sortDirection = signal<SortDirection>('desc');
  currentPage = signal(1);
  pageSize = 10;

  // -- Logs Tab State --
  logDatePreset = signal<LogDatePreset>('30d');
  logStoreFilter = signal<string>('all');
  logActionFilter = signal<string>('ALL'); 
  logCurrentPage = signal(1);
  logPageSize = signal(25);
  logTotalCount = signal(0);
  isLogsLoading = signal(false);
  expandedLogId = signal<string | null>(null);

  // -- Reconciliation State --
  reconcileChargeId = signal('');
  isReconciling = signal(false);

  // -- Store Manager Modal State --
  isStoreModalOpen = signal(false);
  selectedStore = signal<StoreSummary | null>(null);
  walletAdjAmount = signal<number>(0);
  walletAdjReason = signal<string>('');
  isProcessingAction = signal(false);

  isDownloadingBackup = signal(false);

  // -- Confirmation Modal State --
  confirmModalState = signal({
    isOpen: false,
    title: '',
    message: '',
    confirmButtonText: 'Confirm',
    confirmButtonClass: 'bg-red-600 hover:bg-red-700',
    action: null as (() => Promise<void>) | null
  });

  // -- Computed Data Logic for Overview Tab --
  filteredAndSortedStores = computed(() => {
      let data = this.stores();
      const query = this.searchQuery().toLowerCase().trim();
      const status = this.statusFilter();
      const plan = this.planFilter();
      const col = this.sortColumn();
      const dir = this.sortDirection();

      if (query) {
          data = data.filter(store => 
              store.name.toLowerCase().includes(query) || 
              (store.plan_name && store.plan_name.toLowerCase().includes(query)) ||
              store.sub_status.toLowerCase().includes(query)
          );
      }

      if (status !== 'all') {
        data = data.filter(s => s.sub_status === status);
      }

      if (plan !== 'all') {
          data = data.filter(s => s.plan_name === plan);
      }

      return data.sort((a, b) => {
          let valA = a[col]; let valB = b[col];
          if (valA == null) valA = ''; if (valB == null) valB = '';
          if (typeof valA === 'string') { valA = valA.toLowerCase(); valB = (valB as string).toLowerCase(); }
          if (valA < valB) return dir === 'asc' ? -1 : 1;
          if (valA > valB) return dir === 'asc' ? 1 : -1;
          return 0;
      });
  });
  
  totalPages = computed(() => Math.ceil(this.filteredAndSortedStores().length / this.pageSize));
  paginatedStores = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize;
    return this.filteredAndSortedStores().slice(start, start + this.pageSize);
  });
  pageInfo = computed(() => {
    const total = this.filteredAndSortedStores().length;
    if (total === 0) return 'No stores found';
    const start = (this.currentPage() - 1) * this.pageSize + 1;
    const end = Math.min(start + this.pageSize - 1, total);
    return `Showing ${start}-${end} of ${total} stores`;
  });

  // -- Computed Data Logic for Logs Tab --
  logDateRange = computed(() => {
    const end = new Date();
    const start = new Date();
    const preset = this.logDatePreset();
    if (preset === '7d') start.setDate(end.getDate() - 6);
    else if (preset === '30d') start.setDate(end.getDate() - 29);
    else if (preset === '90d') start.setDate(end.getDate() - 89);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  });

  logTotalPages = computed(() => Math.ceil(this.logTotalCount() / this.logPageSize()));
  logPageInfo = computed(() => {
    const total = this.logTotalCount();
    if (total === 0) return 'No logs found';
    const start = (this.logCurrentPage() - 1) * this.logPageSize() + 1;
    const end = Math.min(start + this.logPageSize() - 1, total);
    return `Showing ${start}-${end} of ${total} logs`;
  });
  
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);
  currentUser = this.authService.currentUser;

  constructor() {
    effect(() => {
        if (this.activeTab() === 'logs') {
            this.loadLogs();
        }
    });
  }

  ngOnInit(): void { this.loadData(); }

  switchTab(tab: DashboardTab) {
      this.activeTab.set(tab);
      // Removed loadPlans() call here as it's handled in loadData() for the filter dropdown
  }

  async loadData() {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    try {
        const [statsData, storesData, plansData] = await Promise.all([
            this.platformService.getGlobalStats(),
            this.platformService.getAllStores(),
            this.platformService.getPlans() // Still needed for Overview filter
        ]);
        this.stats.set(statsData);
        this.stores.set(storesData);
        this.plans.set(plansData);
    } catch (e: any) {
        this.errorMessage.set(e.message || 'System Error');
    } finally {
        this.isLoading.set(false);
    }
  }

  async loadLogs() {
    this.isLogsLoading.set(true);
    try {
        const { start, end } = this.logDateRange();
        const storeId = this.logStoreFilter() === 'all' ? undefined : this.logStoreFilter();

        const result = await this.platformService.getGlobalLogs({
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            storeId: storeId,
            actionType: this.logActionFilter(), 
            pageSize: this.logPageSize(),
            pageNumber: this.logCurrentPage()
        });

        this.logs.set(result.logs || []);
        this.logTotalCount.set(result.total_count || 0);
    } catch (e) {
        this.notificationService.showError("Failed to load logs: " + (e as Error).message);
    } finally {
        this.isLogsLoading.set(false);
    }
  }
  
  // -- Log Interactions --
  onLogStoreFilterChange(event: Event) { this.logStoreFilter.set((event.target as HTMLSelectElement).value); this.logCurrentPage.set(1); this.loadLogs(); }
  onLogActionFilterChange(event: Event) { this.logActionFilter.set((event.target as HTMLSelectElement).value); this.logCurrentPage.set(1); this.loadLogs(); }
  toggleMetadata(id: string) { this.expandedLogId.update(current => current === id ? null : id); }
  
  // -- Store Management Actions --
  openStoreManager(store: StoreSummary) { this.selectedStore.set(store); this.walletAdjAmount.set(0); this.walletAdjReason.set(''); this.isStoreModalOpen.set(true); }
  closeStoreManager() { this.isStoreModalOpen.set(false); this.selectedStore.set(null); }
  
  processWalletAdjustment() {
      const store = this.selectedStore(); 
      const amount = this.walletAdjAmount(); 
      const reason = this.walletAdjReason().trim();
      
      if (!store || amount === 0 || !reason) { 
          this.notificationService.showError("กรุณาระบุยอดเงินและเหตุผล"); 
          return; 
      }
      
      this.confirmModalState.set({
          isOpen: true,
          title: 'ยืนยันการปรับยอดเงิน',
          message: `ยืนยันการปรับยอดเงิน ${amount} บาท ให้กับร้าน "${store.name}"?`,
          confirmButtonText: 'ยืนยัน (Confirm)',
          confirmButtonClass: 'bg-indigo-600 hover:bg-indigo-700',
          action: async () => {
              this.isProcessingAction.set(true);
              try { 
                  await this.platformService.adjustStoreWallet(store.id, amount, reason); 
                  this.notificationService.showSuccess("ปรับยอดเงินสำเร็จ"); 
                  this.closeStoreManager(); 
                  this.loadData(); 
              } 
              catch (e: any) { 
                  this.notificationService.showError("Failed: " + e.message); 
              } 
              finally { 
                  this.isProcessingAction.set(false); 
              }
          }
      });
  }

  toggleStoreStatus() {
      const store = this.selectedStore(); if (!store) return;
      const newStatus = store.store_status === 'active' ? 'suspended' : 'active';
      const action = newStatus === 'active' ? 'เปิดใช้งาน (Activate)' : 'ระงับการใช้งาน (Suspend)';
      const btnClass = newStatus === 'active' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700';

      this.confirmModalState.set({
          isOpen: true,
          title: `ยืนยันการ${action}`,
          message: `ยืนยันการ ${action} ร้าน "${store.name}"?`,
          confirmButtonText: 'ยืนยัน (Confirm)',
          confirmButtonClass: btnClass,
          action: async () => {
              this.isProcessingAction.set(true);
              try { 
                  await this.platformService.toggleStoreStatus(store.id, newStatus); 
                  this.notificationService.showSuccess(`สถานะเปลี่ยนเป็น ${newStatus} เรียบร้อยแล้ว`); 
                  this.closeStoreManager(); 
                  this.loadData(); 
              } 
              catch (e: any) { 
                  this.notificationService.showError("Failed: " + e.message); 
              } 
              finally { 
                  this.isProcessingAction.set(false); 
              }
          }
      });
  }

  grantTrial(storeId: string, storeName: string) {
      this.confirmModalState.set({
          isOpen: true,
          title: 'ยืนยันการเพิ่มเวลาทดลองใช้',
          message: `คุณต้องการเพิ่มเวลาทดลองใช้ 14 วันให้กับร้าน "${storeName}" ใช่หรือไม่?`,
          confirmButtonText: 'เพิ่มเวลา (Grant)',
          confirmButtonClass: 'bg-emerald-600 hover:bg-emerald-700',
          action: async () => {
              try { 
                  await this.platformService.extendTrial(storeId, 14); 
                  this.notificationService.showSuccess("เพิ่มเวลาสำเร็จแล้ว (Extended 14 Days)"); 
                  this.loadData(); 
              } 
              catch(e: any) { 
                  this.notificationService.showError("เกิดข้อผิดพลาด: " + (e.message || JSON.stringify(e))); 
              }
          }
      });
  }

  // -- Payment Reconciliation --
  async reconcilePayment() {
    const id = this.reconcileChargeId().trim(); if (!id) return;
    this.isReconciling.set(true);
    try { const result = await this.billingService.manualCheckPayment(id); this.notificationService.showSuccess(`Status: ${result.message}`); this.reconcileChargeId.set(''); this.loadData(); } 
    catch (e: any) { this.notificationService.showError("Reconciliation Failed: " + e.message); } 
    finally { this.isReconciling.set(false); }
  }
  
  // -- Backup --
  downloadBackup() {
    this.confirmModalState.set({
        isOpen: true,
        title: 'ยืนยันการสำรองข้อมูล',
        message: 'This will generate a full database backup. This process may take a few moments. Continue?',
        confirmButtonText: 'Download Backup',
        confirmButtonClass: 'bg-blue-600 hover:bg-blue-700',
        action: async () => {
            this.isDownloadingBackup.set(true);
            try {
                const sqlData = await this.platformService.createFullBackup();
                const blob = new Blob([sqlData], { type: 'application/sql' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `chuamkan_backup_${new Date().toISOString().split('T')[0]}.sql`;
                document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
                this.notificationService.showSuccess('Backup download started!');
            } catch (e: any) { 
                this.notificationService.showError('Backup failed: ' + e.message); 
            } finally { 
                this.isDownloadingBackup.set(false); 
            }
        }
    });
  }

  closeConfirmModal() {
    this.confirmModalState.update(s => ({ ...s, isOpen: false }));
  }

  async onConfirmAction() {
    const action = this.confirmModalState().action;
    if (action) await action();
    this.closeConfirmModal();
  }

  // -- Interaction Methods for Overview Tab --
  onSearch(event: Event) { this.searchQuery.set((event.target as HTMLInputElement).value); this.currentPage.set(1); }
  setStatusFilter(status: string) { this.statusFilter.set(status); this.currentPage.set(1); }
  setPlanFilter(planName: string) { this.planFilter.set(planName); this.currentPage.set(1); }
  toggleSort(column: SortColumn) { if (this.sortColumn() === column) { this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc'); } else { this.sortColumn.set(column); this.sortDirection.set('asc'); } }
  nextPage(): void { if (this.currentPage() < this.totalPages()) this.currentPage.update(p => p + 1); }
  previousPage(): void { if (this.currentPage() > 1) this.currentPage.update(p => p - 1); }
  getSortIcon(column: SortColumn): string { if (this.sortColumn() !== column) return 'fa-sort text-slate-600 opacity-30'; return this.sortDirection() === 'asc' ? 'fa-sort-up text-purple-500' : 'fa-sort-down text-purple-500'; }

  // -- Interaction Methods for Logs Tab --
  setLogDatePreset(preset: LogDatePreset) { this.logDatePreset.set(preset); this.logCurrentPage.set(1); this.loadLogs(); }
  nextLogPage() { if (this.logCurrentPage() < this.logTotalPages()) { this.logCurrentPage.update(p => p + 1); this.loadLogs(); }}
  previousLogPage() { if (this.logCurrentPage() > 1) { this.logCurrentPage.update(p => p - 1); this.loadLogs(); }}

  logout() { this.authService.logout(); }

  // -- Visual Helpers --
  getActionColor(type: string): string {
      if (type.includes('VIEW') || type.includes('REVEAL')) return 'bg-blue-900/50 text-blue-400 border-blue-500/30';
      if (type.includes('CREATE') || type.includes('ADD')) return 'bg-emerald-900/50 text-emerald-400 border-emerald-500/30';
      if (type.includes('UPDATE') || type.includes('EDIT')) return 'bg-orange-900/50 text-orange-400 border-orange-500/30';
      if (type.includes('DELETE') || type.includes('REMOVE') || type.includes('SUSPEND')) return 'bg-red-900/50 text-red-400 border-red-500/30';
      if (type.includes('LOGIN')) return 'bg-purple-900/50 text-purple-400 border-purple-500/30';
      return 'bg-slate-800 text-slate-400 border-slate-600';
  }

  getStatusClass(status: string): string {
      switch(status) {
          case 'active': return 'bg-green-500/10 text-green-400 border-green-500/20';
          case 'past_due': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
          case 'canceled': return 'bg-red-500/10 text-red-400 border-red-500/20';
          case 'trialing': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
          default: return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
      }
  }
  // Kept here as it's used in Overview tab as well
  getPlanClass(planName: string | undefined): string {
      const name = (planName || '').toLowerCase();
      if (name.includes('enterprise') || name.includes('tier l')) return 'bg-purple-900/30 text-purple-300 border-purple-500/30';
      if (name.includes('growth') || name.includes('tier m')) return 'bg-blue-900/30 text-blue-300 border-blue-500/30';
      return 'bg-slate-800 text-slate-400 border-slate-600';
  }
}
